/**
 * Shop TV display helpers (pure) — used by pages/ShopTvDisplay.jsx.
 *
 * The page is opened by a Raspberry Pi in Chromium kiosk mode at /tv/<token> and
 * runs for weeks without anyone touching it, so everything here is about staying
 * correct unattended: the theme comes from the server (resolved for the shop's
 * month), the slide clock survives refreshes, and a new deploy is picked up by
 * comparing the main bundle the page loaded with the one index.html now names.
 */

// Palettes are drawn on a dark background on purpose: a bright wall of white is
// tiring on a big TV across a shop. Each theme only swaps colours and an accent.
export const THEMES = {
  default: {
    bg1: "#0b1220", bg2: "#14213d", panel: "rgba(255,255,255,0.06)", line: "rgba(255,255,255,0.12)",
    text: "#f8fafc", muted: "#94a3b8", accent: "#f59e0b", accent2: "#38bdf8", bar: "#f59e0b",
    decor: [],
  },
  autumn: {
    bg1: "#1a0f07", bg2: "#2e1a0b", panel: "rgba(255,237,213,0.07)", line: "rgba(255,237,213,0.14)",
    text: "#fff7ed", muted: "#d6b894", accent: "#f97316", accent2: "#facc15", bar: "#ea580c",
    decor: ["🍂", "🍁"],
  },
  halloween: {
    bg1: "#0c0814", bg2: "#22113a", panel: "rgba(255,255,255,0.06)", line: "rgba(255,122,0,0.25)",
    text: "#fff7ed", muted: "#c4b5fd", accent: "#ff7a00", accent2: "#a855f7", bar: "#ff7a00",
    decor: ["🎃", "🦇", "👻"],
  },
  holiday: {
    bg1: "#06180f", bg2: "#0e2c1c", panel: "rgba(255,255,255,0.06)", line: "rgba(255,255,255,0.14)",
    text: "#f8fafc", muted: "#a7c4b5", accent: "#ef4444", accent2: "#22c55e", bar: "#dc2626",
    decor: ["🎄", "❄️", "🎁"],
  },
  winter: {
    bg1: "#08121f", bg2: "#10243f", panel: "rgba(255,255,255,0.06)", line: "rgba(186,230,253,0.18)",
    text: "#f0f9ff", muted: "#93c5fd", accent: "#7dd3fc", accent2: "#e0f2fe", bar: "#38bdf8",
    decor: ["❄️"],
  },
  spring: {
    bg1: "#0a1710", bg2: "#13291c", panel: "rgba(255,255,255,0.06)", line: "rgba(187,247,208,0.18)",
    text: "#f0fdf4", muted: "#a7d7b8", accent: "#4ade80", accent2: "#f9a8d4", bar: "#22c55e",
    decor: ["🌱", "🌷"],
  },
  summer: {
    bg1: "#07162a", bg2: "#0c2c47", panel: "rgba(255,255,255,0.06)", line: "rgba(254,240,138,0.2)",
    text: "#f8fafc", muted: "#9cc3dd", accent: "#facc15", accent2: "#38bdf8", bar: "#eab308",
    decor: ["☀️", "🇺🇸"],
  },
};

export function themeFor(name) {
  return THEMES[name] || THEMES.default;
}

export const MEDALS = { 1: "🥇", 2: "🥈", 3: "🥉" };

/** Seconds a slide stays up; a one-slide display never rotates. */
export function slideDuration(slide) {
  const s = Number(slide?.duration_seconds);
  return Number.isFinite(s) && s >= 5 ? s : 30;
}

/**
 * Index of the slide to show after `current` once its time is up. Stays in range
 * when a refresh shortened the list (a slide was removed while showing).
 */
export function nextSlideIndex(current, count) {
  if (!count || count < 1) return 0;
  return (Math.max(0, current) + 1) % count;
}

export function clampSlideIndex(current, count) {
  if (!count || count < 1) return 0;
  return current >= 0 && current < count ? current : 0;
}

/** The hashed main bundle a page or an index.html refers to, e.g. "main.3d79dc35.js". */
export function mainBundleName(text) {
  const m = /\/static\/js\/(main\.[0-9a-f]+\.js)/.exec(text || "");
  return m ? m[1] : null;
}

/**
 * True only when BOTH bundles are known and differ — a dev server (no hash), a
 * failed fetch or a captive-portal page must never trigger a reload loop.
 */
export function isNewBuild(loadedBundle, latestIndexHtml) {
  const latest = mainBundleName(latestIndexHtml);
  return Boolean(loadedBundle && latest && latest !== loadedBundle);
}

/**
 * Rows whose count went up since the previous refresh -> {id: gained}. Keyed by
 * the operator id the API sends; a first load (no previous data) flags nothing.
 */
export function gains(prevRows, rows) {
  if (!Array.isArray(prevRows) || !Array.isArray(rows)) return {};
  const before = new Map(prevRows.map((r) => [r.id, r.count]));
  const out = {};
  for (const r of rows) {
    const b = before.get(r.id);
    if (b !== undefined && r.count > b) out[r.id] = r.count - b;
  }
  return out;
}

/** Bar width as a % of the leader's count (the leader's bar is full). */
export function barPercent(count, leaderCount) {
  if (!leaderCount || leaderCount <= 0 || !count || count <= 0) return 0;
  return Math.max(3, Math.min(100, (count / leaderCount) * 100));
}

export function formatClock(d) {
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" });
}

export function formatDay(d) {
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "America/Chicago" });
}

export function formatNumber(n) {
  return Number(n || 0).toLocaleString("en-US");
}
